import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import {debounce} from 'lodash';
import PropTypes from 'prop-types';
import Card from 'common/components/card';
import CardConfig from 'common/components/card-config';
import Colors from 'common/constants/colors';
import {htmlUnescape, markdownToJsx} from 'common/utils/string-util';
import './index.scss';

export default class ImageCard extends React.Component {
  static propTypes = {
    backgroundColor: PropTypes.string,
    backgroundPosition: PropTypes.string,
    backgroundSize: PropTypes.string,
    caption: PropTypes.bool,
    captionDescription: PropTypes.string,
    captionDescriptionFontSize: PropTypes.string,
    captionDescriptionTargetBlank: PropTypes.bool,
    captionTitle: PropTypes.string,
    captionTitleFontSize: PropTypes.string,
    cardHeightAsImage: PropTypes.bool,
    connectDragSource: PropTypes.func,
    customCardHeight: PropTypes.number,
    disableConfig: PropTypes.bool,
    imageAlt: PropTypes.string,
    imageHeight: PropTypes.number,
    imageWidth: PropTypes.number,
    reportId: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    srcParams: PropTypes.string,
  };

  static defaultProps = {
    captionDescriptionTargetBlank: true,
    connectDragSource: (x) => x,
    disableConfig: true,
    srcParams: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      imageLoaded: false,
      isCaptionCentered: false,
    };

    this.onResize = this.onResize.bind(this);
  }

  componentDidMount() {
    this.observer = new ResizeObserver(debounce(this.onResize, 250));
    this.observer.observe(this._node);
    this.setProportions();
  }

  UNSAFE_componentWillReceiveProps() {
    this.setProportions();
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  getSpacerHeight() {
    if (this.state.isNarrow) {
      return 20;
    }

    if (!this.state.isCaptionCentered) {
      // Gives the text a top padding of 15%
      return this._caption.clientHeight * 0.15;
    }

    // Centers the text vertically
    return (this._caption.clientHeight - this._captionText.clientHeight) / 2;
  }

  setProportions() {
    if (!this._node || !this._caption) {
      return;
    }

    const {caption} = this.props;

    const isCaptionCentered = !this._caption
      ? false
      : this._caption.clientHeight === this._caption.scrollHeight;

    const cardWidth = this._node.clientWidth;
    const isNarrow = cardWidth < 768;

    this.setState({
      captionSpacerHeight: caption ? this.getSpacerHeight() : null,
      imageLoaded: true,
      isCaptionCentered,
      isNarrow,
    });
  }

  render() {
    const {
      backgroundColor,
      backgroundPosition,
      backgroundSize,
      caption,
      captionDescription,
      captionDescriptionTargetBlank,
      captionDescriptionFontSize,
      captionTitle,
      captionTitleFontSize,
      cardHeightAsImage,
      connectDragSource,
      customCardHeight,
      imageAlt,
      imageHeight,
      disableConfig,
      reportId,
      src,
      srcParams,
    } = this.props;

    const {isNarrow} = this.state;

    const height = `${cardHeightAsImage ? imageHeight : customCardHeight}px`;

    return (
      <Card className={'ImageCard Card--corner-menu'} loading={false}>
        <div
          ref={(node) => (this._node = node)}
          style={{
            borderRadius: '5px',
            backgroundColor:
              backgroundSize === 'contain' ? Colors.gray900 : 'auto',
            overflow: 'hidden',
          }}>
          {connectDragSource(
            <div
              style={{
                backgroundColor: backgroundColor || Colors.gray900,
                backgroundImage: `url(${src}?${srcParams})`,
                backgroundPosition: `${backgroundPosition} center`,
                backgroundRepeat: 'no-repeat',
                backgroundSize,
                overflow: 'hidden',
                bottom: 0,
                lineHeight: height,
                height,
                width: caption && !isNarrow ? '67%' : '100%',
              }}>
              <img
                alt={imageAlt || captionTitle || ''}
                onLoad={this.onResize}
                ref={(node) => (this._img = node)}
                src={`${src}?${srcParams}`}
                style={{opacity: 0}}
              />
            </div>,
          )}

          {caption ? (
            <div style={{display: this.state.imageLoaded ? 'block' : 'none'}}>
              <div
                className={'ImageCardCaption CardCaption'}
                ref={(node) => (this._caption = node)}
                style={{
                  borderRadius: isNarrow ? '0 0 5px 5px' : '0 5px 5px 0',
                  position: isNarrow ? 'static' : 'absolute',
                  width: !isNarrow ? '33%' : '100%',
                }}>
                <div>
                  <div
                    className={'ImageCardCaption-spacer'}
                    style={{
                      height: `${this.state.captionSpacerHeight}px`,
                    }}
                  />

                  <div
                    className={'ImageCardCaption-text'}
                    ref={(node) => (this._captionText = node)}>
                    <h1
                      key={0}
                      style={{
                        fontSize: captionTitleFontSize,
                        paddingBottom: '20px',
                      }}>
                      {htmlUnescape(captionTitle)}
                    </h1>

                    <div style={{fontSize: captionDescriptionFontSize}}>
                      {markdownToJsx(htmlUnescape(captionDescription), {
                        targetBlank: captionDescriptionTargetBlank,
                      })}
                    </div>
                  </div>

                  {this.state.isCaptionCentered && !isNarrow ? null : (
                    <div
                      className={'ImageCardCaption-spacer'}
                      style={{
                        height: `${this.state.captionSpacerHeight}px`,
                      }}
                    />
                  )}
                </div>
              </div>

              {isNarrow ? null : (
                <span>
                  <div className={'ImageCardCaptionTopFader'} />
                  <div className={'ImageCardCaptionBottomFader'} />
                </span>
              )}
            </div>
          ) : null}
        </div>

        {!disableConfig ? (
          <CardConfig
            className={'ImageCard-CardConfig'}
            disableDownloadCsv={true}
            disableDownloadPointsCsv={true}
            reportId={reportId}
          />
        ) : null}
      </Card>
    );
  }

  onResize() {
    this.setProportions();
  }
}
